import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FaCreditCard } from "react-icons/fa";
import { IndianRupee } from "lucide-react";
import apiClient from "../../utils/apiClient";
import useRazorpay from "../../hooks/useRazorpay";
import CommonToaster from "../Common/CommonToaster";
import { AppButton } from "../ui";
import { convertToIndianCurrency } from "../../utils/common";
import "../Fees/payment.scss";

/**
 * StudentSelfPay — Modal-only self-payment component for the student profile.
 * Controlled by parent via show / onHide props.
 *
 * Props:
 *   student          — Student document (with totalFees, paidFees, pendingFees)
 *   show             — Boolean controlling modal visibility
 *   onHide           — Callback to close the modal
 *   onPaymentSuccess — Callback to refresh student data after payment
 */
const StudentSelfPay = ({ student, show = false, onHide, onPaymentSuccess }) => {
  const [amount, setAmount] = useState("");
  const [processing, setProcessing] = useState(false);
  const [rzpEnabled, setRzpEnabled] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const pending = parseFloat(student?.pendingFees) || 0;

  useEffect(() => {
    apiClient
      .get("/payments/config")
      .then(({ data }) => setRzpEnabled(!!data.enabled))
      .catch(() => setRzpEnabled(false))
      .finally(() => setConfigLoading(false));
  }, []);

  const handlePay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      setToast({ show: true, message: "Enter a valid amount.", type: "error" });
      return;
    }
    if (amt > pending) {
      setToast({ show: true, message: `Amount cannot exceed ₹${pending}.`, type: "error" });
      return;
    }

    setProcessing(true);
    try {
      const { data: orderData } = await apiClient.post("/payments/create-order", {
        studentId: student._id,
        amount: amt,
        description: "Fee Payment (Self)",
      });

      openCheckout(
        orderData,
        async (response) => {
          try {
            await apiClient.post("/payments/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              transactionId: orderData.transactionId,
            });
            setToast({ show: true, message: "Payment successful! Receipt will be emailed.", type: "success" });
            if (onHide) onHide();
            setAmount("");
            if (onPaymentSuccess) onPaymentSuccess();
          } catch {
            setToast({ show: true, message: "Payment received but verification failed. Contact admin.", type: "error" });
          }
          setProcessing(false);
        },
        (error) => {
          setToast({ show: true, message: error?.reason || "Payment cancelled.", type: "error" });
          setProcessing(false);
        },
      );
    } catch (ex) {
      const msg = ex?.response?.data?.message || "Could not initiate payment.";
      setToast({ show: true, message: msg, type: "error" });
      setProcessing(false);
    }
  };

  if (configLoading) return null;
  if (!rzpEnabled || pending <= 0) return null;

  const dueAfter = Math.max(pending - (parseFloat(amount) || 0), 0);

  return (
    <>
      {/* Pay Modal */}
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <IndianRupee size={20} /> Pay Online
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="pay-modal__field">
            <label htmlFor="pay-amount">Amount to Pay</label>
            <input
              id="pay-amount"
              type="number"
              min={1}
              max={pending}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`Max ₹${pending}`}
            />
            <span className="pay-modal__hint">
              Minimum ₹1 &bull; Maximum ₹{pending.toLocaleString("en-IN")}
            </span>
          </div>

          <div className="pay-modal__summary">
            <div className="pay-modal__summary-row">
              <span>Current Pending</span>
              <span>{convertToIndianCurrency(pending)}</span>
            </div>
            <div className="pay-modal__summary-row">
              <span>Paying Now</span>
              <span>{convertToIndianCurrency(parseFloat(amount) || 0)}</span>
            </div>
            <div className="pay-modal__summary-row">
              <span>Due After</span>
              <span>{convertToIndianCurrency(dueAfter)}</span>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onHide}
            disabled={processing || rzpLoading}
          >
            Cancel
          </button>
          <AppButton
            onClick={handlePay}
            disabled={processing || rzpLoading || !amount}
          >
            {(processing || rzpLoading) ? (
              <><Spinner size="sm" className="me-1" /> Processing...</>
            ) : (
              <><FaCreditCard className="me-1" /> Pay ₹{parseFloat(amount) || 0}</>
            )}
          </AppButton>
        </Modal.Footer>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </>
  );
};

export default StudentSelfPay;
